// import api's and queries
import { useGetAuxValueGroups } from 'queries';
import { AUX_VALUE } from 'RestAPIs/endpoints';
import { useSaveResource } from 'RestAPIs/useSaveResource';

// import components
import { LoadingButton } from '@mui/lab';
import Box from '@mui/material/Box';
import { ControlledCheckbox, ControlledSelect } from 'components/shared';
import { ControlledColorPickerPopover } from 'components/shared/ControlledFields/ControlledColorPickerPopover';
import TextField from 'components/shared/ControlledFields/ControlledTextField';
import MultiStepForm from 'components/shared/MultiStepForm/MultiStepForm';
import { showSuccessSnackbar } from 'components/shared/Snackbar';
import TranslationSection from './TranslationSection';

// import hooks and react
import { yupResolver } from '@hookform/resolvers/yup';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import useGetAuxTranslationsByAuxValueId from '../hooks/useGetAuxTranslationsByAuxValueId';

// import schema
import { schema } from './schema';

// import types
import type { FormComponentProps } from 'components/shared/CRUD/TableCRUDLayout';
import type { AuxValue } from 'RestAPIs/AuxiliaryValues';
import type { AuxFormValues } from './AuxValuesForm.types';

// import utils
import { isNil } from 'ramda';
import mapToOptions from 'utils/mapToOptions';
import { convertPayloadForSave } from './utils';
import handleSaveTranslations from './utils/handleSaveTranslations';
export interface IAuxValuesFormProps extends FormComponentProps {
  auxValue?: AuxValue;
}

const AuxValuesForm = ({
  handleClose,
  rowId,
  auxValue
}: IAuxValuesFormProps) => {
  'use memo';

  const { t } = useTranslation('common');
  const { data: auxValueGroups } = useGetAuxValueGroups();
  const translations = useGetAuxTranslationsByAuxValueId(rowId);
  const convertedGroups = auxValueGroups?.map((item) => {
    return {
      ...item,
      name: t(`auxGroups.${item.code}`)
    };
  });

  const methods = useForm<AuxFormValues>({
    defaultValues: schema.cast({
      ...auxValue,
      translations: isNil(translations) ? [] : translations
    }),
    resolver: yupResolver(schema)
  });
  const { control, handleSubmit, formState, reset } = methods;

  const { mutateAsync: save, isLoading } = useSaveResource({
    url: AUX_VALUE,
    id: rowId,
    invalidateQueryKey: ['Auxiliary', 'list']
  });

  useEffect(() => {
    if (!isNil(translations)) {
      reset(
        schema.cast({
          ...auxValue,
          translations: isNil(translations) ? [] : translations
        })
      );
    }
  }, [auxValue, reset, translations]);

  const onSubmit = (data: AuxFormValues) => {
    const payload = convertPayloadForSave(data, auxValueGroups);

    save(payload).then((response) =>
      handleSaveTranslations(
        auxValue?.auxValueId ? auxValue.auxValueId : response.data.auxValueId,
        formState,
        payload.translations
      ).then(() => {
        handleClose();
        showSuccessSnackbar();
      })
    );
  };

  return (
    <>
      <MultiStepForm onSubmit={handleSubmit(onSubmit)} methods={methods}>
        <MultiStepForm.Section
          title={t('auxValues.formSectionLabels.basis')}
          validateFields={['code', 'auxValueGroupName']}
        >
          <Box gap={2.5}>
            <ControlledSelect
              name="auxValueGroupId"
              control={control}
              variant="outlined"
              label={t('auxValues.group')}
              options={mapToOptions(
                convertedGroups ?? [],
                'auxValueGroupId',
                'name'
              )}
            />
          </Box>
          <Box display="flex" gap={2.5}>
            <TextField
              name="code"
              control={control}
              variant="outlined"
              label={t('auxValues.code')}
            />
          </Box>
          <Box display="flex" gap={2.5}>
            <TextField
              name="sortOrder"
              control={control}
              variant="outlined"
              label={t('auxValues.sortOrder')}
            />
          </Box>
          <Box display="flex" gap={2.5}>
            <ControlledCheckbox
              control={control}
              name={`notInUse`}
              label={t('auxValues.notInUse')}
            />
          </Box>
          <Box display="flex" gap={2.5}>
            <ControlledColorPickerPopover
              name="colorCode"
              label={t('auxValues.color')}
            />
          </Box>
        </MultiStepForm.Section>

        <TranslationSection control={control} formState={formState} />

        <MultiStepForm.Actions>
          <Box display="flex" ml="auto">
            <LoadingButton
              loading={isLoading}
              type="submit"
              variant="contained"
              autoFocus
              color="primary"
            >
              {t('save')}
            </LoadingButton>
          </Box>
        </MultiStepForm.Actions>
      </MultiStepForm>
    </>
  );
};

export default AuxValuesForm;
